import { useRef, useImperativeHandle, forwardRef } from "react";
import { Bold, Italic, List, Code, Link2, ListOrdered, Quote } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Minimal markdown-toolbar textarea.
 * Inserts/wraps Markdown syntax at the current selection.
 */
const RichTextarea = forwardRef(function RichTextarea(
  { value, onChange, placeholder, rows = 3, "data-testid": testId, autoFocus, className },
  ref,
) {
  const taRef = useRef(null);
  useImperativeHandle(ref, () => taRef.current);

  const wrap = (left, right = left, placeholderText = "texto") => {
    const ta = taRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const sel = value.slice(start, end) || placeholderText;
    const next = value.slice(0, start) + left + sel + right + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      const cursor = start + left.length;
      ta.setSelectionRange(cursor, cursor + sel.length);
    });
  };

  const prefixLines = (prefix) => {
    const ta = taRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const before = value.slice(0, start);
    const sel = value.slice(start, end) || "elemento";
    const after = value.slice(end);
    const block = sel.split("\n").map((l, i) => `${prefix.replace("{n}", i + 1)}${l}`).join("\n");
    const next = before + block + after;
    onChange(next);
    requestAnimationFrame(() => ta.focus());
  };

  const insertLink = () => {
    const ta = taRef.current;
    if (!ta) return;
    const url = window.prompt("URL", "https://");
    if (!url) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const sel = value.slice(start, end) || "enlace";
    const next = value.slice(0, start) + `[${sel}](${url})` + value.slice(end);
    onChange(next);
  };

  return (
    <div className={cn("rounded-xl border border-white/60 dark:border-white/10 bg-white/60 dark:bg-slate-900/40 overflow-hidden focus-within:ring-2 focus-within:ring-blue-500", className)}>
      <div className="flex items-center gap-0.5 px-1.5 py-1 border-b border-white/40 dark:border-white/5">
        <ToolbarBtn icon={Bold} onClick={() => wrap("**", "**", "negrita")} label="Negrita" testId="rt-bold" />
        <ToolbarBtn icon={Italic} onClick={() => wrap("_", "_", "cursiva")} label="Cursiva" testId="rt-italic" />
        <ToolbarBtn icon={Code} onClick={() => wrap("`", "`", "código")} label="Código" testId="rt-code" />
        <span className="w-px h-4 bg-white/40 dark:bg-white/10 mx-1" />
        <ToolbarBtn icon={List} onClick={() => prefixLines("- ")} label="Lista" testId="rt-ul" />
        <ToolbarBtn icon={ListOrdered} onClick={() => prefixLines("{n}. ")} label="Lista numerada" testId="rt-ol" />
        <ToolbarBtn icon={Quote} onClick={() => prefixLines("> ")} label="Cita" testId="rt-quote" />
        <ToolbarBtn icon={Link2} onClick={insertLink} label="Enlace" testId="rt-link" />
      </div>
      <textarea
        ref={taRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        autoFocus={autoFocus}
        data-testid={testId}
        className="w-full bg-transparent resize-y px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none"
      />
    </div>
  );
});

function ToolbarBtn({ icon: Icon, onClick, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      data-testid={testId}
      className="h-7 w-7 rounded-md grid place-items-center text-muted-foreground hover:bg-white/50 dark:hover:bg-white/10 hover:text-foreground transition"
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}

export default RichTextarea;
